import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import './AgentOrders.css';

function AgentOrders({ token }) {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  // Check if Flux domain
  const isFluxDomain = window.location.hostname.includes('fluxleads') ||
    window.location.hostname.includes('flux');

  // Step 1: Decode token just to get the campaign, then fetch full profile from API
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      const campaign = localStorage.getItem('selectedCampaign') || decoded.selectedCampaign || 'medicare';
      setSelectedCampaign(campaign);
    } catch (error) {
      console.error('Error decoding token:', error);
      toast.error('Invalid session. Please login again.');
    }
  }, [token]);

  // Step 2: Fetch full agent profile from the API (has name, phone, email, etc.)
  useEffect(() => {
    if (!token) return;
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/api/agent/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const p = res.data;
        const fullName = p.name || '';
        const parts = fullName.trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';
        setUserInfo({
          email: p.email || '',
          name: fullName,
          phone: p.phone || '',
          firstName,
          lastName,
          agentType: p.agentType || 'individual',
          ghlLocationId: p.ghlLocationId,
          ghlLocationName: p.ghlLocationName
        });
      } catch (err) {
        console.error('Error fetching agent profile:', err);
        // Fallback: try to get what we can from the token itself
        try {
          const decoded = jwtDecode(token);
          const fullName = decoded.name || '';
          const parts = fullName.trim().split(' ');
          setUserInfo({
            email: decoded.email || '',
            name: fullName,
            phone: decoded.phone || '',
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
            agentType: decoded.agentType || 'individual',
            ghlLocationId: decoded.ghlLocationId,
            ghlLocationName: decoded.ghlLocationName
          });
        } catch (_) {}
      }
    };
    fetchProfile();
  }, [token]);

  useEffect(() => {
    if (selectedCampaign && userInfo) {
      fetchOrders();
    }
  }, [selectedCampaign, userInfo]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/orders/my-orders?campaignId=${selectedCampaign}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setOrders(response.data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', class: 'status-pending' },
      approved: { label: 'Approved', class: 'status-approved' },
      rejected: { label: 'Rejected', class: 'status-rejected' },
      invoice_sent: { label: 'Invoice Sent', class: 'status-invoice' },
      completed: { label: 'Completed', class: 'status-completed' }
    };
    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleDetails = (orderId) => {
    const row = document.getElementById(`details-${orderId}`);
    if (row) {
      row.style.display = row.style.display === 'none' ? 'table-row' : 'none';
    }
  };

  // Build GHL form URL with pre-filled agent data
  const getOrderFormUrl = () => {
    const baseUrl = 'https://hello.fluxprotocol.ai/widget/form/Kx9uqAz9JC2hSZ8LVzrN';
    if (!userInfo) return baseUrl;

    const params = new URLSearchParams();
    if (userInfo.firstName) params.append('first_name', userInfo.firstName);
    if (userInfo.lastName) params.append('last_name', userInfo.lastName);
    if (userInfo.email) params.append('email', userInfo.email);
    if (userInfo.phone) params.append('phone', userInfo.phone);

    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  };

  if (!selectedCampaign) {
    return (
      <div className={`agent-orders-container ${isFluxDomain ? 'flux-theme' : ''}`}>
        <div className="no-campaign-message">
          <h3>No Campaign Selected</h3>
          <p>Please select a campaign to view and place orders.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`agent-orders-container ${isFluxDomain ? 'flux-theme' : ''}`}>

      {/* ── Order Form (embedded GHL form) ── */}
      <div className="order-form-section">
        <div className="orders-header">
          <h2>Order Opportunities</h2>
          <p className="order-form-subtitle">
            Complete the form below to purchase opportunities.
          </p>
        </div>

        <div className="ghl-form-wrapper">
          <iframe
            src={getOrderFormUrl()}
            title="Order Opportunities Form"
            className="order-form-iframe"
            allow="fullscreen"
          />
        </div>
      </div>

      {/* ── Orders History ── */}
      <div className="orders-list">
        <div className="orders-list-header">
          <h3>Your Order History</h3>
          <button
            className="btn-refresh"
            onClick={fetchOrders}
            disabled={loading}
            title="Refresh orders"
          >
            {loading ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>
        </div>

        {loading && <div className="loading">Loading orders…</div>}

        {!loading && orders.length === 0 && (
          <div className="no-orders">
            <p>No orders yet. Use the form above to place your first order!</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Leads</th>
                  <th>Price/Lead</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.orderId}>
                    <tr className="order-row">
                      <td className="order-id">
                        {order.orderId.substring(0, 8)}…
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td className="text-center">{order.leadsQuantity}</td>
                      <td>${(order.pricePerLead || 0).toFixed(2)}</td>
                      <td className="order-total">${(order.totalPrice || 0).toFixed(2)}</td>
                      <td>{getStatusBadge(order.status)}</td>
                      <td>
                        <button
                          className="btn-view-details"
                          onClick={() => toggleDetails(order.orderId)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                    <tr
                      id={`details-${order.orderId}`}
                      className="order-details-row"
                      style={{ display: 'none' }}
                    >
                      <td colSpan="7">
                        <div className="order-details">
                          <div className="detail-section">
                            <h4>Order Information</h4>
                            <div className="detail-grid">
                              <div className="detail-item">
                                <label>Campaign:</label>
                                <span>{order.campaignName || order.campaignId}</span>
                              </div>
                              <div className="detail-item">
                                <label>Order ID:</label>
                                <span className="monospace">{order.orderId}</span>
                              </div>
                              <div className="detail-item">
                                <label>Created:</label>
                                <span>{formatDate(order.createdAt)}</span>
                              </div>
                              {order.source && (
                                <div className="detail-item">
                                  <label>Source:</label>
                                  <span>{order.source === 'payment_webhook' ? 'Payment (Auto)' : order.source}</span>
                                </div>
                              )}
                              {order.updatedAt !== order.createdAt && (
                                <div className="detail-item">
                                  <label>Last Updated:</label>
                                  <span>{formatDate(order.updatedAt)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {order.note && (
                            <div className="detail-section">
                              <h4>Notes</h4>
                              <p className="order-note">{order.note}</p>
                            </div>
                          )}

                          {order.adminResponse && (
                            <div className="detail-section admin-response">
                              <h4>Response</h4>
                              <p>{order.adminResponse}</p>
                              {order.adminRespondedBy && order.adminRespondedBy !== 'system' && (
                                <small>
                                  By: {order.adminRespondedBy} on {formatDate(order.adminRespondedAt)}
                                </small>
                              )}
                            </div>
                          )}

                          {order.status === 'rejected' && !order.adminResponse && (
                            <div className="detail-section">
                              <p className="rejected-message">
                                This order was rejected. Please contact admin for more information.
                              </p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentOrders;
